<template>
  <div
    class="md:py-1 sticky !top-[59px] md:!top-[55px] z-20 bg-body-bg transition-all duration-300"
    ref="categoriesRow"
    :class="{ 'category-row': isAtTop }"
  >
    <div class="md:container">
      <Swiper
        class="categories-swiper"
        :slides-per-view="7"
        :centered-slides="false"
        :slides-per-group-skip="1"
        :space-between="12"
        :navigation="true"
        :modules="[Navigation, Mousewheel]"
        :mousewheel="{
          forceToAxis: true,
          sensitivity: 1,
        }"
        :loop="false"
        :breakpoints="breakpoints"
        @swiper="onSwiper"
      >
        <template v-if="categoryList?.length">
          <SwiperSlide
            v-for="categoryItem in activeCategoriesList"
            :key="`${categoryItem.id}-active`"
            class="py-2 !w-fit mx-1"
          >
            <CategoryTag
              :slug="categoryItem.slug"
              :title="categoryItem?.title"
              :icon="categoryItem.icon_url"
              :is-active="true"
              @activate-category="updateURL($event)"
            />
          </SwiperSlide>
          <SwiperSlide
            v-if="activeCategoriesList.length > 0"
            class="py-2 !w-fit !h-14 flex items-center mx-1"
          >
            <div class="bg-gray-2 h-9 w-[1px] mt-0.5" />
          </SwiperSlide>
          <SwiperSlide
            v-for="categoryItem in inActiveCategoriesList"
            :key="categoryItem.id"
            class="py-2 !w-fit mx-1"
          >
            <CategoryTag
              :slug="categoryItem.slug"
              :title="categoryItem?.title"
              :icon="categoryItem.icon_url"
              :is-active="false"
              @activate-category="updateURL($event)"
            />
          </SwiperSlide>
        </template>
      </Swiper>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, watch } from "vue";
import { Navigation, Mousewheel } from "swiper/modules";

import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css";
import "swiper/css/navigation";
const localePath = useLocalePath();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const props = defineProps({
  categoryList: { type: Array, required: true },
});

const emit = defineEmits(["scroll-to-activities"]);

const activeCategoriesSlug = ref([]);
const swiperInstance = ref(null);
const breakpoints = reactive({
  320: {
    slidesPerView: "auto",
    slidesPerGroup: 1,
    spaceBetween: 6,
  },
  960: {
    slidesPerView: "auto",
    slidesPerGroup: 1,
    spaceBetween: 12,
  },
  1264: {
    slidesPerView: "auto",
    slidesPerGroup: 1,
  },
  1904: {
    slidesPerView: "auto",
    slidesPerGroup: 1,
  },
});

const onSwiper = (swiper) => {
  swiperInstance.value = swiper;
};

const scrollToActiveCategory = () => {
  if (swiperInstance.value) {
    if (activeCategoriesSlug.value.length) {
      swiperInstance.value.slideTo(0);
      emit("scroll-to-activities");
    } else {
      swiperInstance.value.slideTo(0);
    }
  }
};

watch(
  () => route.query,
  () => {
    activeCategoriesSlug.value = route.query.category?.split(",") ?? [];
    scrollToActiveCategory();
  },
  { deep: true, immediate: true }
);

const inActiveCategoriesList = computed(() =>
  props.categoryList.filter(
    (category) =>
      !activeCategoriesSlug.value.find((slug) => slug == category.slug)
  )
);
const activeCategoriesList = computed(() =>
  props.categoryList.filter((category) =>
    activeCategoriesSlug.value.find((slug) => slug == category.slug)
  )
);

const updateURL = (categorySlug) => {
  const currentQuery = { ...router.currentRoute.value.query };
  if (activeCategoriesSlug.value.find((slug) => slug == categorySlug)) {
    activeCategoriesSlug.value = activeCategoriesSlug.value.filter(
      (slug) => slug != categorySlug
    );
  } else {
    activeCategoriesSlug.value.push(categorySlug);
  }
  if (activeCategoriesSlug.value.length == 0) delete currentQuery.category;
  else {
    currentQuery.category = activeCategoriesSlug.value.join(",");
  }
  router.push(
    localePath({ name: "things-to-do-in-slug", query: currentQuery })
  );
};

const categoriesRow = ref(null);
const isAtTop = ref(false);

const checkPosition = () => {
  if (categoriesRow.value) {
    const rect = categoriesRow.value.getBoundingClientRect();
    isAtTop.value = rect.top == 55;
  }
};

const handleScroll = () => {
  checkPosition();
};

let body;

onMounted(() => {
  body = document.getElementsByTagName("body")[0];
  body.addEventListener("scroll", handleScroll);

  checkPosition();
});

onBeforeUnmount(() => {
  body.removeEventListener("scroll", handleScroll);
});
</script>

<style lang="scss">
.categories-swiper .swiper-button-next {
  @apply w-[65px] h-[45px] ps-5 max-md:hidden;
  background: linear-gradient(270deg, $bodyBg 50%, rgba(245, 248, 250, 0) 100%);
  :dir(rtl) & {
    background: linear-gradient(
      90deg,
      $bodyBg 50%,
      rgba(245, 248, 250, 0) 100%
    );
  }
}
.categories-swiper .swiper-button-prev {
  @apply w-[65px] h-[45px] pe-5 max-md:hidden;
  background: linear-gradient(90deg, $bodyBg 50%, rgba(245, 248, 250, 0) 100%);
  :dir(rtl) & {
    background: linear-gradient(
      270deg,
      $bodyBg 50%,
      rgba(245, 248, 250, 0) 100%
    );
  }
}
.categories-swiper .swiper-button-next {
  right: 0 !important;
  left: auto !important;

  :dir(rtl) & {
    left: 0 !important;
    right: auto !important;
  }
}
.categories-swiper .swiper-button-prev {
  left: 0 !important;
  right: auto !important;

  :dir(rtl) & {
    left: auto !important;
    right: 0 !important;
  }
}

.categories-swiper .swiper-button-next::after,
.categories-swiper .swiper-button-prev::after {
  content: "" !important;
  background-image: url("~/assets/icons/chevron.svg");
  background-size: 22px !important;
  @apply bg-white border border-gray-2 rounded-full shadow-3 max-md:hidden top-[55%] w-[38px] h-[38px] bg-contain bg-no-repeat bg-center text-gray-1;
}
.categories-swiper .swiper-button-prev::after {
  @apply ltr:rotate-180;
}
.categories-swiper .swiper-button-next::after {
  @apply rtl:rotate-180;
}
.categories-swiper .swiper-button-prev.swiper-button-disabled,
.categories-swiper .swiper-button-next.swiper-button-disabled {
  @apply hidden;
}
.categories-swiper .category-filter-button::after {
  content: "";
  @apply w-[1px] h-11 absolute bg-gray-2 -right-[10px];
}

.category-row {
  box-shadow: 0px 4px 10px 0px #0000000d;
}
</style>
